import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { costingAPI } from '../../../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useConfirm } from '@/components/ConfirmProvider';
import { toast } from 'sonner';
import { Plus, Trash2, Loader2, Save, FlaskConical, AlertTriangle, CheckCircle2, ArrowUp, ArrowDown, ArrowUpDown, Import, GripVertical, History, GitCompare } from 'lucide-react';
import { GREEN, RUST, MUTED, fmtRate, fmt2, fmtDateTime } from './helpers';


export const FormulationsSection = ({ products = [], refreshKey }) => {
  const confirm = useConfirm();
  const [rms, setRms] = useState([]);
  const [formulations, setFormulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);


  const [productId, setProductId] = useState('');
  const [batchSize, setBatchSize] = useState('1000');
  const [rows, setRows] = useState([]); // [{rm_id, percent}]
  const [currentFormId, setCurrentFormId] = useState(null);
  const [fSortKey, setFSortKey] = useState('product_name');
  const [fSortDir, setFSortDir] = useState('asc');
  const [importFrom, setImportFrom] = useState('');
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [selVer, setSelVer] = useState([]); // version_ids chosen for compare (max 2)
  const [compareOpen, setCompareOpen] = useState(false);
  const [qtyEdit, setQtyEdit] = useState({ idx: null, val: '' });
  const [rowSort, setRowSort] = useState({ key: null, dir: 'asc' });
  const pctRefs = useRef([]);
  const qtyRefs = useRef([]);
  const rmRefs = useRef([]);
  const qtyMovedRef = useRef(false);


  const sellableProducts = useMemo(
    () => products.filter((p) => !p.is_virtual && p.name !== 'MISC').sort((a, b) => a.name.localeCompare(b.name)),
    [products],
  );
  const rmById = useMemo(() => Object.fromEntries(rms.map((r) => [r.id, r])), [rms]);


  const loadLists = useCallback(async () => {
    setLoading(true);
    try {
      const [rmRes, formRes] = await Promise.all([costingAPI.listRawMaterials(), costingAPI.listFormulations()]);
      setRms(rmRes.data || []);
      setFormulations(formRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, []);


  useEffect(() => { loadLists(); }, [loadLists, refreshKey]);

  const loadVersions = useCallback(async (pid) => {
    if (!pid) { setVersions([]); setSelVer([]); return; }
    setLoadingVersions(true);
    try {
      const res = await costingAPI.listFormulationVersions(pid);
      setVersions(res.data || []);
    } catch (e) { /* non-blocking */ }
    finally { setLoadingVersions(false); }
  }, []);

  useEffect(() => { loadVersions(productId); setSelVer([]); }, [productId, loadVersions]);

  const toggleVer = (vid) => setSelVer((prev) => {
    if (prev.includes(vid)) return prev.filter((x) => x !== vid);
    if (prev.length >= 2) return [prev[1], vid];
    return [...prev, vid];
  });


  const loadForProduct = useCallback(async (pid) => {
    setProductId(pid);
    if (!pid) { setRows([]); setCurrentFormId(null); setBatchSize('1000'); return; }
    try {
      const res = await costingAPI.getFormulationByProduct(pid);
      if (res.data) {
        setCurrentFormId(res.data.id);
        setBatchSize(String(res.data.batch_size_kg || 1000));
        setRows((res.data.rows || []).map((r) => ({ rm_id: r.rm_id, percent: String(r.percent) })));
      } else {
        setCurrentFormId(null);
        setBatchSize('1000');
        setRows([]);
      }
    } catch (e) {
      toast.error('Failed to load formulation');
    }
  }, []);


  const batch = parseFloat(batchSize) || 0;
  const percentTotal = useMemo(() => rows.reduce((a, r) => a + (parseFloat(r.percent) || 0), 0), [rows]);


  const derived = useMemo(() => rows.map((r) => {
    const rm = rmById[r.rm_id];
    const pct = parseFloat(r.percent) || 0;
    const rate = Number(rm?.rate_per_kg || 0);
    const kg100 = pct;
    const kgBatch = (pct / 100) * batch;
    return { ...r, rm, rate, kg100, kgBatch, cost: kgBatch * rate };
  }), [rows, rmById, batch]);


  const rmCostAtBatch = useMemo(() => derived.reduce((a, d) => a + d.cost, 0), [derived]);
  const rmCostAt100 = useMemo(() => derived.reduce((a, d) => a + d.kg100 * d.rate, 0), [derived]);


  const addRow = () => setRows((p) => [...p, { rm_id: '', percent: '' }]);
  const updateRow = (i, patch) => setRows((p) => p.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => setRows((p) => p.filter((_, idx) => idx !== i));

  // Drag-and-drop manual reorder of the recipe rows. Hold the grip handle and
  // drop onto another row to move it up/down. Clears any active column sort so
  // the hand-arranged order is what gets saved.
  const dragIndexRef = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const onRowDragStart = (i) => (e) => {
    dragIndexRef.current = i;
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(i)); } catch (_) {}
  };
  const onRowDragOver = (i) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIdx !== i) setDragOverIdx(i);
  };
  const onRowDrop = (i) => (e) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    dragIndexRef.current = null;
    setDragOverIdx(null);
    if (from === null || from === undefined || from === i) return;
    setRows((prev) => {
      const next = prev.slice();
      const [moved] = next.splice(from, 1);
      next.splice(i, 0, moved);
      return next;
    });
    setRowSort({ key: null, dir: 'asc' });
  };
  const onRowDragEnd = () => { dragIndexRef.current = null; setDragOverIdx(null); };


  // Reorder the recipe rows by the chosen RM attribute when the header is clicked.
  const sortRowsBy = (key) => {
    const dir = rowSort.key === key && rowSort.dir === 'asc' ? 'desc' : 'asc';
    setRowSort({ key, dir });
    const mul = dir === 'asc' ? 1 : -1;
    const numeric = key === 'percent' || key === 'qty'; // qty is proportional to percent
    setRows((prev) => prev.slice().sort((a, b) => {
      if (numeric) {
        return ((parseFloat(a.percent) || 0) - (parseFloat(b.percent) || 0)) * mul;
      }
      const av = `${(rmById[a.rm_id] || {})[key] || ''}`.toLowerCase();
      const bv = `${(rmById[b.rm_id] || {})[key] || ''}`.toLowerCase();
      return av.localeCompare(bv) * mul;
    }));
  };
  const SortArrow = ({ k }) => (rowSort.key === k
    ? (rowSort.dir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
    : <ArrowUpDown className="w-3 h-3 opacity-40" />);


  // Qty @batch is editable; committing it back-computes the % (and vice-versa).
  const commitQty = (i) => {
    if (qtyEdit.idx !== i) return;
    const q = parseFloat(qtyEdit.val);
    const b = parseFloat(batchSize) || 0;
    if (!isNaN(q) && q >= 0 && b > 0) {
      const pct = Math.round((q / b) * 100 * 10000) / 10000;
      updateRow(i, { percent: String(pct) });
    }
    setQtyEdit({ idx: null, val: '' });
  };


  const importable = useMemo(
    () => formulations.filter((f) => f.product_id !== productId),
    [formulations, productId],
  );
  const importRecipe = (sourcePid) => {
    const src = formulations.find((f) => f.product_id === sourcePid);
    if (!src) return;
    setRows((src.rows || []).map((r) => ({ rm_id: r.rm_id, percent: String(r.percent) })));
    setBatchSize(String(src.batch_size_kg || 1000));
    setImportFrom('');
    toast.success(`Imported recipe from ${src.product_name} — review & Save`);
  };


  const save = async () => {
    if (!productId) { toast.error('Pick a product first'); return; }
    if (batch <= 0) { toast.error('Batch size must be greater than zero'); return; }
    const clean = rows.filter((r) => r.rm_id).map((r) => ({ rm_id: r.rm_id, percent: parseFloat(r.percent) || 0 }));
    if (clean.length === 0) { toast.error('Add at least one raw material'); return; }
    setSaving(true);
    try {
      const res = await costingAPI.upsertFormulation({ product_id: productId, batch_size_kg: batch, rows: clean });
      setCurrentFormId(res.data.id);
      toast.success('Formulation saved');
      await loadLists();
      await loadVersions(productId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };


  const deleteFormulation = async (f) => {
    const ok = await confirm({ title: 'Delete formulation?', description: `Recipe for "${f.product_name}" will be removed.`, confirmLabel: 'Delete', destructive: true });
    if (!ok) return;
    try {
      await costingAPI.deleteFormulation(f.id);
      setFormulations((p) => p.filter((x) => x.id !== f.id));
      if (f.product_id === productId) { setProductId(''); setRows([]); setCurrentFormId(null); }
      toast.success('Formulation deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };


  const pctOk = Math.abs(percentTotal - 100) < 0.01;


  const sortedFormulations = useMemo(() => {
    const dir = fSortDir === 'asc' ? 1 : -1;
    const numOf = (f) => {
      switch (fSortKey) {
        case 'rms': return f.rows?.length || 0;
        case 'percent_total': return f.percent_total || 0;
        case 'batch_size_kg': return f.batch_size_kg || 0;
        case 'rm_cost_at_batch': return f.rm_cost_at_batch || 0;
        default: return 0;
      }
    };
    return formulations.slice().sort((a, b) => {
      if (fSortKey === 'product_name') return `${a.product_name}`.localeCompare(`${b.product_name}`) * dir;
      if (fSortKey === 'updated_at') return `${a.updated_at || ''}`.localeCompare(`${b.updated_at || ''}`) * dir;
      return (numOf(a) - numOf(b)) * dir;
    });
  }, [formulations, fSortKey, fSortDir]);


  const toggleFSort = (k) => {
    if (fSortKey === k) setFSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setFSortKey(k); setFSortDir('asc'); }
  };
  const FSortHead = ({ label, k, align = 'left' }) => (
    <th
      className={`px-3 py-2 font-semibold cursor-pointer select-none ${align === 'right' ? 'text-right' : 'text-left'}`}
      onClick={() => toggleFSort(k)}
      data-testid={`formulation-sort-${k}`}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        {fSortKey === k
          ? (fSortDir === 'asc' ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />)
          : <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />}
      </span>
    </th>
  );


  return (
    <div>
      {loading ? (
        <div className="flex items-center gap-2 py-16 justify-center" style={{ color: MUTED }}><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : (
        <div className="space-y-4">
          {/* Editor — full width */}
          <Card className="p-4">
              <div className="flex flex-wrap items-end gap-3 mb-4">
                <div className="flex-1 min-w-52">
                  <label className="text-xs font-medium block mb-1" style={{ color: MUTED }}>Finished Product</label>
                  <Select value={productId} onValueChange={loadForProduct}>
                    <SelectTrigger data-testid="formulation-product-select"><SelectValue placeholder="Select a product" /></SelectTrigger>
                    <SelectContent>
                      {sellableProducts.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-40">
                  <label className="text-xs font-medium block mb-1" style={{ color: MUTED }}>Editable Batch (kg)</label>
                  <Input data-testid="formulation-batch-size" type="number" min="1" step="1" value={batchSize} onChange={(e) => setBatchSize(e.target.value)} />
                </div>
                {productId && importable.length > 0 && (
                  <div className="w-60">
                    <label className="text-xs font-medium block mb-1 flex items-center gap-1" style={{ color: MUTED }}><Import className="w-3.5 h-3.5" /> Import recipe from</label>
                    <Select value={importFrom} onValueChange={importRecipe}>
                      <SelectTrigger data-testid="formulation-import-select"><SelectValue placeholder="Another product…" /></SelectTrigger>
                      <SelectContent>
                        {importable.map((f) => (<SelectItem key={f.product_id} value={f.product_id}>{f.product_name} <span style={{ color: MUTED }}>· {f.rows?.length || 0} RMs</span></SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>


              {!productId ? (
                <p className="py-8 text-center text-sm" style={{ color: MUTED }}>Select a product to build or edit its recipe.</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr style={{ background: '#FBF9F6', color: MUTED }} className="text-left text-xs uppercase">
                          <th className="px-1 py-2 w-8" />
                          <th className="px-2 py-2">
                            <div className="inline-flex items-center gap-2">
                              <span>Raw Material</span>
                              <button type="button" onClick={() => sortRowsBy('name')} className="inline-flex items-center gap-0.5 normal-case hover:text-[#2B5339]" data-testid="formulation-sort-rm-name">
                                Name <SortArrow k="name" />
                              </button>
                              <button type="button" onClick={() => sortRowsBy('category')} className="inline-flex items-center gap-0.5 normal-case hover:text-[#2B5339]" data-testid="formulation-sort-rm-category">
                                Category <SortArrow k="category" />
                              </button>
                            </div>
                          </th>
                          <th className="px-2 py-2 text-right">
                            <button type="button" onClick={() => sortRowsBy('percent')} className="inline-flex items-center gap-0.5 ml-auto normal-case hover:text-[#2B5339]" data-testid="formulation-sort-percent">
                              % <SortArrow k="percent" />
                            </button>
                          </th>
                          <th className="px-2 py-2 text-right">Qty @100kg</th>
                          <th className="px-2 py-2 text-right">
                            <button type="button" onClick={() => sortRowsBy('qty')} className="inline-flex items-center gap-0.5 ml-auto normal-case hover:text-[#2B5339]" data-testid="formulation-sort-qty">
                              Qty @{batch || 0}kg <SortArrow k="qty" />
                            </button>
                          </th>
                          <th className="px-2 py-2 text-right">Rate</th>
                          <th className="px-2 py-2 text-right">Cost @batch</th>
                          <th className="px-2 py-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {derived.map((d, i) => (
                          <tr
                            key={i}
                            className="border-t"
                            style={{ borderColor: '#F0EEE9', background: dragOverIdx === i ? '#EFF4EE' : undefined }}
                            data-testid="formulation-row"
                            onDragOver={onRowDragOver(i)}
                            onDrop={onRowDrop(i)}
                          >
                            <td
                              className="px-1 py-1.5 text-center align-middle"
                              draggable
                              onDragStart={onRowDragStart(i)}
                              onDragEnd={onRowDragEnd}
                              style={{ cursor: 'grab' }}
                              title="Drag to reorder"
                              data-testid="formulation-drag-handle"
                            >
                              <GripVertical className="w-4 h-4 inline" style={{ color: '#B9BEB6' }} />
                            </td>
                            <td className="px-2 py-1.5 min-w-48">
                              <Select
                                value={d.rm_id}
                                onValueChange={(v) => {
                                  updateRow(i, { rm_id: v });
                                  // On selecting a raw material (incl. Enter in the
                                  // dropdown), jump focus to the next row's RM picker
                                  // for rapid data entry. Delay lets Radix finish
                                  // closing + restoring focus first.
                                  const nx = rmRefs.current[i + 1];
                                  if (nx) setTimeout(() => { try { nx.focus(); } catch (_) {} }, 80);
                                }}
                              >
                                <SelectTrigger ref={(el) => { rmRefs.current[i] = el; }} className="h-8" data-testid="formulation-rm-select"><SelectValue placeholder="Pick RM" /></SelectTrigger>
                                <SelectContent>
                                  {rms.map((rm) => (<SelectItem key={rm.id} value={rm.id}>{rm.name} <span style={{ color: MUTED }}>· {rm.category}</span></SelectItem>))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-2 py-1.5 text-right w-24">
                              <Input
                                ref={(el) => { pctRefs.current[i] = el; }}
                                className="h-8 text-right" type="number" step="0.01" min="0"
                                value={d.percent}
                                onChange={(e) => updateRow(i, { percent: e.target.value })}
                                onFocus={(e) => e.target.select()}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const nx = pctRefs.current[i + 1];
                                    if (nx) setTimeout(() => { nx.focus(); nx.select(); }, 0);
                                    else e.target.blur();
                                  }
                                }}
                                data-testid="formulation-percent"
                              />
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums" style={{ color: MUTED }}>{fmt2(d.kg100)}</td>
                            <td className="px-2 py-1.5 text-right w-28">
                              <Input
                                ref={(el) => { qtyRefs.current[i] = el; }}
                                className="h-8 text-right" type="number" step="0.01" min="0"
                                value={qtyEdit.idx === i ? qtyEdit.val : fmt2(d.kgBatch)}
                                onFocus={(e) => { setQtyEdit({ idx: i, val: String(Math.round(d.kgBatch * 100) / 100) }); e.target.select(); }}
                                onChange={(e) => setQtyEdit({ idx: i, val: e.target.value })}
                                onBlur={() => { if (qtyMovedRef.current) { qtyMovedRef.current = false; return; } commitQty(i); }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    qtyMovedRef.current = true;
                                    commitQty(i);
                                    const nx = qtyRefs.current[i + 1];
                                    if (nx) {
                                      // Focus next row, then re-select AFTER the
                                      // field swaps into edit mode so its content
                                      // stays highlighted (single rAF loses it).
                                      requestAnimationFrame(() => {
                                        nx.focus();
                                        requestAnimationFrame(() => { try { nx.select(); } catch (_) {} });
                                      });
                                    } else qtyMovedRef.current = false;
                                  }
                                }}
                                data-testid="formulation-qty"
                              />
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums" style={{ color: MUTED }}>{fmtRate(d.rate)}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums font-semibold" style={{ color: GREEN }}>{fmtRate(d.cost)}</td>
                            <td className="px-2 py-1.5">
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeRow(i)} data-testid="formulation-remove-row">
                                <Trash2 className="w-4 h-4" style={{ color: RUST }} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2" style={{ borderColor: GREEN, background: '#FBF9F6' }}>
                          <td className="px-1 py-2" />
                          <td className="px-2 py-2 font-bold" style={{ color: GREEN }}>TOTAL</td>
                          <td className="px-2 py-2 text-right font-bold" style={{ color: pctOk ? GREEN : RUST }}>{fmt2(percentTotal)}%</td>
                          <td className="px-2 py-2 text-right tabular-nums" style={{ color: MUTED }}>{fmt2(rmCostAt100 ? derived.reduce((a, d) => a + d.kg100, 0) : 0)}</td>
                          <td className="px-2 py-2 text-right tabular-nums">{fmt2(derived.reduce((a, d) => a + d.kgBatch, 0))}</td>
                          <td />
                          <td className="px-2 py-2 text-right font-bold" style={{ color: GREEN }}>{fmtRate(rmCostAtBatch)}</td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>


                  <div className="flex flex-wrap items-center gap-3 mt-3">
                    <Button variant="outline" className="h-9" onClick={addRow} data-testid="formulation-add-row"><Plus className="w-4 h-4 mr-1.5" /> Add RM</Button>
                    <div className="flex items-center gap-1.5 text-xs" style={{ color: pctOk ? GREEN : RUST }}>
                      {pctOk ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                      {pctOk ? '% adds up to 100' : `% total is ${fmt2(percentTotal)} (should be 100)`}
                    </div>
                    <div className="flex-1" />
                    <div className="text-xs text-right" style={{ color: MUTED }}>
                      RM cost @100kg: <span className="font-semibold" style={{ color: GREEN }}>{fmtRate(rmCostAt100)}</span>
                    </div>
                    <Button className="h-9 text-white" style={{ background: GREEN }} onClick={save} disabled={saving} data-testid="formulation-save">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
                      {currentFormId ? 'Update Recipe' : 'Save Recipe'}
                    </Button>
                  </div>
                </>
              )}
            </Card>


          {/* Recipe versions — auto-saved on every save, last 10, recomputed at today's rates */}
          {productId && (
            <Card className="p-4" data-testid="formulation-versions-card">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <p className="text-sm font-semibold flex items-center gap-2" style={{ color: GREEN }}>
                  <History className="w-4 h-4" /> Recipe Versions <span className="font-normal" style={{ color: MUTED }}>(last 10, cost at today&rsquo;s rates)</span>
                </p>
                <Button
                  size="sm"
                  className="h-8 text-white"
                  style={{ background: selVer.length === 2 ? GREEN : '#B9BEB6' }}
                  disabled={selVer.length !== 2}
                  onClick={() => setCompareOpen(true)}
                  data-testid="formulation-compare-btn"
                >
                  <GitCompare className="w-4 h-4 mr-1.5" /> Compare selected ({selVer.length}/2)
                </Button>
              </div>
              {loadingVersions ? (
                <div className="flex items-center gap-2 py-6 justify-center text-sm" style={{ color: MUTED }}><Loader2 className="w-4 h-4 animate-spin" /> Loading versions…</div>
              ) : versions.length === 0 ? (
                <p className="text-sm py-4 text-center" style={{ color: MUTED }}>No saved versions yet. Save this recipe to start keeping a history.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ background: '#FBF9F6', color: MUTED }} className="text-xs uppercase tracking-wide text-left">
                        <th className="px-3 py-2 w-10">Pick</th>
                        <th className="px-3 py-2">Saved</th>
                        <th className="px-3 py-2">By</th>
                        <th className="px-3 py-2 text-right">RMs</th>
                        <th className="px-3 py-2 text-right">% Total</th>
                        <th className="px-3 py-2 text-right">Batch (kg)</th>
                        <th className="px-3 py-2 text-right">RM Cost @batch</th>
                        <th className="px-3 py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {versions.map((v, vi) => (
                        <tr key={v.version_id} className="border-t" style={{ borderColor: '#F0EEE9', background: selVer.includes(v.version_id) ? '#EFF4EE' : undefined }} data-testid="formulation-version-row">
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selVer.includes(v.version_id)}
                              onChange={() => toggleVer(v.version_id)}
                              data-testid={`formulation-version-pick-${vi}`}
                            />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {fmtDateTime(v.saved_at)}
                            {vi === 0 && <Badge className="ml-2" style={{ background: GREEN, color: 'white' }}>Latest</Badge>}
                          </td>
                          <td className="px-3 py-2" style={{ color: MUTED }}>{v.saved_by_name || '—'}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{v.rows?.length || 0}</td>
                          <td className="px-3 py-2 text-right tabular-nums" style={{ color: Math.abs((v.percent_total || 0) - 100) < 0.01 ? MUTED : RUST }}>{fmt2(v.percent_total)}%</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmt2(v.batch_size_kg)}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: GREEN }}>{fmtRate(v.rm_cost_at_batch)}</td>
                          <td className="px-3 py-2 text-right">
                            <Button
                              size="sm" variant="outline" className="h-7 text-xs"
                              onClick={() => {
                                setBatchSize(String(v.batch_size_kg || 1000));
                                setRows((v.rows || []).map((r) => ({ rm_id: r.rm_id, percent: String(r.percent) })));
                                setRowSort({ key: null, dir: 'asc' });
                                toast.success('Version loaded into editor — review & Save to make it current');
                              }}
                              data-testid={`formulation-version-restore-${vi}`}
                            >
                              Load
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}


          {/* Saved formulations — below the sheet, full width, wide short tiles */}
          <Card className="p-4">
              <p className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: GREEN }}><FlaskConical className="w-4 h-4" /> Saved Formulations</p>
              {formulations.length === 0 ? (
                <p className="text-sm py-4 text-center" style={{ color: MUTED }}>None yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ background: '#FBF9F6', color: MUTED }} className="text-xs uppercase tracking-wide">
                        <FSortHead label="Product" k="product_name" />
                        <FSortHead label="RMs" k="rms" align="right" />
                        <FSortHead label="% Total" k="percent_total" align="right" />
                        <FSortHead label="Batch (kg)" k="batch_size_kg" align="right" />
                        <FSortHead label="RM Cost @batch" k="rm_cost_at_batch" align="right" />
                        <FSortHead label="Updated" k="updated_at" align="right" />
                        <th className="px-3 py-2 text-right font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedFormulations.map((f) => (
                        <tr
                          key={f.id}
                          className={`border-t cursor-pointer ${f.product_id === productId ? 'bg-[#EFF4EE]' : 'hover:bg-[#FBF9F6]'}`}
                          style={{ borderColor: '#F0EEE9' }}
                          onClick={() => loadForProduct(f.product_id)}
                          data-testid="formulation-saved-item"
                        >
                          <td className="px-3 py-2 font-medium" style={{ color: '#1E2420' }}>{f.product_name}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{f.rows?.length || 0}</td>
                          <td className="px-3 py-2 text-right tabular-nums" style={{ color: Math.abs((f.percent_total || 0) - 100) < 0.01 ? MUTED : RUST }}>{fmt2(f.percent_total)}%</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmt2(f.batch_size_kg)}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: GREEN }}>{fmtRate(f.rm_cost_at_batch)}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap" style={{ color: MUTED }}>
                            {f.updated_at ? new Date(f.updated_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); deleteFormulation(f); }} data-testid="formulation-delete">
                              <Trash2 className="w-4 h-4" style={{ color: RUST }} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </Card>
        </div>
      )}

      {/* Side-by-side version comparison */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="formulation-compare-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><GitCompare className="w-4 h-4" style={{ color: GREEN }} /> Compare Recipe Versions</DialogTitle>
          </DialogHeader>
          {(() => {
            if (selVer.length !== 2) return null;
            const A = versions.find((v) => v.version_id === selVer[0]);
            const B = versions.find((v) => v.version_id === selVer[1]);
            if (!A || !B) return <p className="text-sm" style={{ color: MUTED }}>Versions not found.</p>;
            const mapOf = (v) => {
              const m = {};
              (v.rows || []).forEach((r) => { m[r.rm_id] = r; });
              return m;
            };
            const mA = mapOf(A); const mB = mapOf(B);
            const order = [];
            const seen = new Set();
            [...(A.rows || []), ...(B.rows || [])].forEach((r) => { if (!seen.has(r.rm_id)) { seen.add(r.rm_id); order.push(r); } });
            const col = (v, label) => (
              <div className="text-xs" style={{ color: MUTED }}>
                <div className="font-semibold" style={{ color: '#1E2420' }}>{label}</div>
                {fmtDateTime(v.saved_at)} · {v.saved_by_name || '—'}
              </div>
            );
            return (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Card className="p-3">{col(A, 'Version A')}</Card>
                  <Card className="p-3">{col(B, 'Version B')}</Card>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="formulation-compare-table">
                    <thead>
                      <tr style={{ background: GREEN, color: 'white' }}>
                        <th className="p-2 text-left">Raw Material</th>
                        <th className="p-2 text-right">A %</th>
                        <th className="p-2 text-right">A ₹@batch</th>
                        <th className="p-2 text-right">B %</th>
                        <th className="p-2 text-right">B ₹@batch</th>
                        <th className="p-2 text-right">Δ %</th>
                        <th className="p-2 text-right">Δ ₹</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.map((r, i) => {
                        const a = mA[r.rm_id]; const b = mB[r.rm_id];
                        const aP = a ? Number(a.percent || 0) : 0;
                        const bP = b ? Number(b.percent || 0) : 0;
                        const aC = a ? Number(a.cost_at_batch || 0) : 0;
                        const bC = b ? Number(b.cost_at_batch || 0) : 0;
                        const dP = bP - aP; const dC = bC - aC;
                        const changed = Math.abs(dP) > 0.0001 || Math.abs(dC) > 0.01;
                        return (
                          <tr key={r.rm_id} className="border-t" style={{ borderColor: '#E2DFD8', background: changed ? '#FFF7E6' : (i % 2 ? '#FBF9F6' : 'white') }}>
                            <td className="p-2">{r.rm_name}{!a && <span style={{ color: '#2B8A4B' }}> (added)</span>}{!b && <span style={{ color: RUST }}> (removed)</span>}</td>
                            <td className="p-2 text-right">{a ? `${fmt2(aP)}%` : '—'}</td>
                            <td className="p-2 text-right">{a ? fmtRate(aC) : '—'}</td>
                            <td className="p-2 text-right">{b ? `${fmt2(bP)}%` : '—'}</td>
                            <td className="p-2 text-right">{b ? fmtRate(bC) : '—'}</td>
                            <td className="p-2 text-right" style={{ color: dP > 0 ? '#2B8A4B' : dP < 0 ? RUST : MUTED }}>{dP > 0 ? '+' : ''}{fmt2(dP)}%</td>
                            <td className="p-2 text-right" style={{ color: dC > 0 ? RUST : dC < 0 ? '#2B8A4B' : MUTED }}>{dC > 0 ? '+' : ''}{fmtRate(dC)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: '#2B5339', color: 'white', fontWeight: 700 }}>
                        <td className="p-2">Total RM cost @batch</td>
                        <td className="p-2 text-right">{fmt2(A.percent_total)}%</td>
                        <td className="p-2 text-right">{fmtRate(A.rm_cost_at_batch)}</td>
                        <td className="p-2 text-right">{fmt2(B.percent_total)}%</td>
                        <td className="p-2 text-right">{fmtRate(B.rm_cost_at_batch)}</td>
                        <td className="p-2 text-right">{fmt2((B.percent_total || 0) - (A.percent_total || 0))}%</td>
                        <td className="p-2 text-right">{(B.rm_cost_at_batch - A.rm_cost_at_batch) > 0 ? '+' : ''}{fmtRate(B.rm_cost_at_batch - A.rm_cost_at_batch)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <p className="text-[11px]" style={{ color: MUTED }}>Note: batch sizes differ between versions when shown — Δ ₹ compares each version&rsquo;s cost at its own saved batch, recomputed at today&rsquo;s RM rates.</p>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};
